import { type CadeLogoVariant } from '../canonical-data.js';

export type { CadeLogoVariant };

export interface CadeLogoProps {
  variant: CadeLogoVariant;
  size?: number | string;
  className?: string;
  title?: string;
  /** Show the hairline ring badge (default). Pass false for the bare glyph. Ignored when `disc`. */
  ring?: boolean;
  /**
   * Render the filled amber-disc "avatar" form (amber disc + dark glyph) instead of
   * the monochrome currentColor mark. Used for the in-product brand mark (the web
   * sidebar). The amber disc is self-contained, so it reads on any surface and the
   * glyph ink stays constant; the disc tone shifts per `variant` to match the product
   * `--color-cade-avatar-bg` token (deeper amber on light paper, brighter on charcoal).
   */
  disc?: boolean;
}

/**
 * OBSIDIAN Cade mark — frozen geometry from the brand handoff.
 *
 * Two forms:
 *  - Monochrome (default): near-black on light / bone on dark (per `variant`),
 *    transparent center + a faint 40% ring. Used for inline message avatars, the
 *    home greeting, and the login/auth badges.
 *  - Amber disc (`disc`): a filled amber disc + dark glyph — the in-product brand
 *    mark (the web sidebar). Mirrors the `--color-cade-avatar-bg` / `--color-avatar-logo`
 *    product tokens; amber is otherwise a UI signal, not used on the monochrome mark.
 */
const VIEWBOX = '0 0 380 380';
const BROW =
  'M 90 200 C 90 140, 125 90, 175 72 C 178 70, 180 68, 180 65 L 180 40 ' +
  'C 180 34, 186 30, 192 32 C 218 40, 240 58, 258 82 C 278 108, 290 150, 292 200 Z';
const BAR = 'M 70 212 C 140 192, 260 192, 335 208';
const LENS_L =
  'M 80 244 L 80 294 Q 80 306, 92 306 L 164 306 Q 176 306, 178 296 ' +
  'L 186 254 Q 188 242, 176 242 L 92 242 Q 80 242, 80 244 Z';
const LENS_R =
  'M 300 244 L 300 294 Q 300 306, 288 306 L 216 306 Q 204 306, 202 296 ' +
  'L 194 254 Q 192 242, 204 242 L 288 242 Q 300 242, 300 244 Z';

// near-black on a light surface, bone on a dark surface
const INK: Record<CadeLogoVariant, string> = { light: '#1a1714', dark: '#ece7df' };
// Amber-disc form: disc tone per surface, constant dark glyph ink. Hardcoded (like
// INK above) because this package is consumed where the app's CSS vars don't exist
// (native/mobile, server/email PNG render). Keep in sync with the web tokens
// --color-cade-avatar-bg (#cf7a33 light / #f0a24a dark) and --color-avatar-logo (#100e0c).
const DISC_BG: Record<CadeLogoVariant, string> = { light: '#cf7a33', dark: '#f0a24a' };
const DISC_INK = '#100e0c';

export function CadeLogo({ variant, size = 128, className, title, ring = true, disc = false }: CadeLogoProps) {
  const role = title ? 'img' : 'presentation';

  if (disc) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={VIEWBOX}
        width={size}
        height={size}
        fill="none"
        className={className}
        role={role}
        aria-label={title}
      >
        <circle cx={190} cy={190} r={190} fill={DISC_BG[variant]} />
        <g fill={DISC_INK}>
          <path d={BROW} />
          <path d={BAR} fill="none" stroke={DISC_INK} strokeWidth={18} strokeLinecap="round" />
          <path d={LENS_L} />
          <path d={LENS_R} />
        </g>
      </svg>
    );
  }

  const color = INK[variant];
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={VIEWBOX}
      width={size}
      height={size}
      fill="none"
      className={className}
      role={role}
      aria-label={title}
    >
      {ring && (
        <circle cx={190} cy={190} r={140} stroke={color} strokeWidth={5} strokeOpacity={0.4} />
      )}
      <g fill={color}>
        <path d={BROW} />
        <path d={BAR} fill="none" stroke={color} strokeWidth={18} strokeLinecap="round" />
        <path d={LENS_L} />
        <path d={LENS_R} />
      </g>
    </svg>
  );
}
